import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, IconButton, TextField, Stack, Typography, Table, TableHead,
    TableBody, TableRow, TableCell, Avatar, Chip, CircularProgress, InputAdornment, Alert, Button,
    ToggleButtonGroup, ToggleButton, Pagination, Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SendIcon from "@mui/icons-material/Send";
import livechatApi from "./livechatApi";
import { getAvatarColor, getInitials } from "./livechatDisplayUtils";
import { PASTEL } from "./livechatTheme";

const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 10;

/**
 * DEV-1197 — menu "Daftar Kontak" mitra: staff purchasing cari mitra by nama/nomor HP (toggle
 * eksplisit, bukan auto-detect), lihat kota/spesialisasi/jam buka, nomor HP tersamar (AC-2b),
 * lalu klik "Kirim Pesan" utk mulai chat (reuse `LivechatAgentService.startSessionToMitra`,
 * sudah ada sejak DEV-1194). Tabel + paginasi mengikuti mockup desain DEV-1190.
 */
export default function MitraContactsDialog({ open, onClose, onStarted }) {
    const [keyword, setKeyword] = useState("");
    const [searchField, setSearchField] = useState("name");
    const [contacts, setContacts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(null); // supplier id yang sedang diproses
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setKeyword("");
        setSearchField("name");
        setPage(1);
        setContacts([]);
        setTotal(0);
        setError(null);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        const timer = setTimeout(() => {
            livechatApi
                .searchMitraContacts({ keyword: keyword.trim(), searchField, page, limit: PAGE_SIZE })
                .then(({ data }) => {
                    setContacts(data.data || []);
                    setTotal(data.additional_data?.total || 0);
                })
                .catch(() => setError("Gagal memuat daftar kontak mitra."))
                .finally(() => setLoading(false));
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [open, keyword, searchField, page]);

    const handleSearchFieldChange = (_e, value) => {
        if (!value) return;
        setSearchField(value);
        setPage(1);
    };

    const handleSelect = async (supplier) => {
        if (!supplier.phone) {
            setError(`${supplier.name} tidak punya nomor HP terdaftar.`);
            return;
        }
        setStarting(supplier.id);
        setError(null);
        try {
            const { data } = await livechatApi.startSessionToMitra({
                name: supplier.name,
                phone: supplier.phone,
            });
            onStarted?.(data.data);
        } catch (e) {
            setError(e?.response?.data?.message || "Gagal memulai chat ke mitra ini.");
        } finally {
            setStarting(null);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(page * PAGE_SIZE, total);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: 560 } }}>
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{
                        width: 36, height: 36, borderRadius: 2, flexShrink: 0, mt: 0.25,
                        display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: PASTEL.primarySoft,
                    }}>
                        <PeopleAltIcon sx={{ color: PASTEL.primary, fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>Daftar Kontak — Mitra</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Cari mitra untuk memulai percakapan baru, tanpa menunggu mitra chat lebih dulu
                        </Typography>
                    </Box>
                </Stack>
                <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ display: "flex", flexDirection: "column", p: 0 }}>
                <Stack direction="row" spacing={1.5} sx={{ p: 2 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Ketik untuk mencari nama atau nomor mitra..."
                        value={keyword}
                        onChange={(e) => { setKeyword(e.target.value); setPage(1); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <ToggleButtonGroup
                        size="small" exclusive value={searchField} onChange={handleSearchFieldChange}
                        sx={{ flexShrink: 0 }}
                    >
                        <ToggleButton value="name" sx={{ textTransform: "none", px: 2 }}>Nama</ToggleButton>
                        <ToggleButton value="phone" sx={{ textTransform: "none", px: 2 }}>Nomor</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                {error && <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

                <Box sx={{ flex: 1, overflowX: "auto" }}>
                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 6 }}>
                            <CircularProgress size={22} />
                        </Stack>
                    ) : contacts.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 6 }}>
                            Tidak ada mitra yang cocok.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Mitra</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Nomor HP</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Spesialisasi</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Jam Buka</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }} align="right">Aksi</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contacts.map((supplier) => (
                                    <TableRow key={supplier.id} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={1.25} alignItems="center">
                                                <Avatar sx={{ bgcolor: getAvatarColor(supplier.name), width: 36, height: 36, fontSize: 14 }}>
                                                    {getInitials(supplier.name)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>{supplier.name}</Typography>
                                                    {supplier.cityName && (
                                                        <Typography variant="caption" color="text.secondary">{supplier.cityName}</Typography>
                                                    )}
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{supplier.phoneMasked || "-"}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                {supplier.specialities?.length > 0
                                                    ? supplier.specialities.map((s) => (
                                                        <Chip key={s} size="small" label={s} sx={{ backgroundColor: PASTEL.primarySoft, fontSize: 11 }} />
                                                    ))
                                                    : <Typography variant="caption" color="text.secondary">-</Typography>}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{supplier.operationalHour || "-"}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small" variant="contained" disabled={starting !== null}
                                                startIcon={starting === supplier.id ? <CircularProgress size={12} color="inherit" /> : <SendIcon sx={{ fontSize: 14 }} />}
                                                onClick={() => handleSelect(supplier)}
                                                sx={{
                                                    textTransform: "none", backgroundColor: PASTEL.primary,
                                                    "&:hover": { backgroundColor: PASTEL.primaryDark },
                                                }}
                                            >
                                                Kirim Pesan
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>

                {!loading && total > 0 && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            Menampilkan {rangeStart}–{rangeEnd} dari {total} mitra
                        </Typography>
                        <Pagination
                            size="small" count={totalPages} page={page}
                            onChange={(_e, value) => setPage(value)}
                        />
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
}
